import { Box } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useMeasurement } from "lib/@hooks/useMeasurement"
import { getTaskAmendsKey } from "authoring/routes/project/amends/lib/required-amends-helpers"
import { findAuthoringTask, normalizeAuthoringTask } from "./lib/authoring-task-lookup"
import { InlineCompareButtons } from "routes/schedule/compare/inline-approval-buttons/inline-compare-buttons"
import { toComparableTask } from "authoring/routes/project/amends/lib/to-comparable-task.mjs"
import { getComparisonNormalizeString } from "routes/schedule/compare/get-comparison-normalizer"
import { compareTwoObjects } from "routes/schedule/compare/task-header-bar"
import { TaskOrHeader } from "routes/schedule/tasks/taskOrHeader"
import { If } from "lib/@components/switch"
import { AuthoringReviewAmendsBlock } from "./authoring-review-amends-block"
import { AuthoringReviewPriorRejection } from "./authoring-review-prior-rejection"
import { Else } from "lib/@components/else"
import { AuthoringSideBySideRow } from "./authoring-side-by-side-row"

export function AuthoringReviewCompareTask({ task }) {
    const [{ width, height }, attach] = useMeasurement()
    const { comparisonTarget, compare, schedule, target } = useBoundContext()
    const otherTask = findAuthoringTask(comparisonTarget.tasks, task)
    const originalTask = findAuthoringTask(compare.tasks, task)
    const editedTask = findAuthoringTask(target.tasks, task)
    const reviewDecisionTask = normalizeAuthoringTask(otherTask ?? originalTask ?? editedTask ?? task)
    const taskAmendsKey = getTaskAmendsKey(otherTask ?? originalTask ?? task)
    const hasDifference = !otherTask || hasTaskFieldChanges(originalTask, otherTask)

    return (
        <Bound acceptId={task.title}>
            <AuthoringSideBySideRow
                className={hasDifference ? "changed-task-row" : "unchanged-task-row"}
                left={
                    <Bound
                        comparisonFunction={compareTwoObjects(task, otherTask ?? task, otherTask ?? task)}
                        target={editedTask}
                        originalTarget={otherTask || task}
                    >
                        <Box ref={attach} width={1}>
                            {!editedTask ? (
                                <Box
                                    alignItems="center"
                                    width={width}
                                    height={110}
                                    bgcolor="action.selected"
                                    display="flex"
                                    justifyContent="center"
                                >
                                    Removed &quot;{task.title}&quot; from schedule &quot;{schedule.title}&quot;
                                </Box>
                            ) : (
                                <TaskOrHeader last={true} item={editedTask} />
                            )}
                        </Box>
                    </Bound>
                }
                right={
                    <Bound
                        originalTarget={task}
                        comparisonFunction={compareTwoObjects(task, otherTask ?? task, otherTask ?? task)}
                    >
                        <If value={otherTask}>
                            <TaskOrHeader last={true} item={otherTask} />
                            <Else>
                                <Box py={1}>
                                    <Box
                                        borderRadius={1}
                                        width={width}
                                        minWidth={180}
                                        p={1}
                                        height={height}
                                        minHeight={64}
                                        textAlign="center"
                                        alignItems="center"
                                        bgcolor="action.selected"
                                        display="flex"
                                        justifyContent="center"
                                    >
                                        Not found in this version
                                    </Box>
                                </Box>
                            </Else>
                        </If>
                    </Bound>
                }
                after={
                    hasDifference ? (
                        <Box className="after" ml={4} mb={1}>
                            <InlineCompareButtons
                                idFromGroup={task.title}
                                to={reviewDecisionTask}
                                from={originalTask}
                            />
                        </Box>
                    ) : null
                }
                below={
                    <>
                        <AuthoringReviewPriorRejection amendsKey={taskAmendsKey} />
                        <If truthy={hasDifference}>
                            <AuthoringReviewAmendsBlock
                                amendsKey={taskAmendsKey}
                                subtitle="Change Task Reason"
                                cyPrefix="side-by-side-amends"
                            />
                        </If>
                    </>
                }
            />
        </Bound>
    )
}

function hasTaskFieldChanges(t1, t2) {
    if (!t1 || !t2) return true
    if (Boolean(t1.isHeader) !== Boolean(t2.isHeader)) return true
    const normalizeString = getComparisonNormalizeString(t1, t2)
    return (
        JSON.stringify(toNormalizedComparableTask(t1, normalizeString)) !==
        JSON.stringify(toNormalizedComparableTask(t2, normalizeString))
    )
}

function toNormalizedComparableTask(task, normalizeString) {
    const comparable = toComparableTask(task)
    comparable.title = normalizeString(comparable.title)
    comparable.content = normalizeString(comparable.content)
    comparable.note = normalizeString(comparable.note)
    return comparable
}
